import type { SymbolImportBinding } from '../../types.js';
import type { SymbolIndexFileEntry } from '../build/collect-file-entries.js';
import { existingLocalNames, resolveExportableNames, STAR_LOCAL_NAME } from './star-reexport-names.js';
import { collectUpstreamEntries } from './star-reexport-upstream.js';

/** Expand star re-exports for one file using lazily resolved upstream entries. */
export function expandStarReexportForFile(
  entry: SymbolIndexFileEntry,
  resolveEntry: (filePath: string) => SymbolIndexFileEntry | undefined,
): SymbolIndexFileEntry {
  const collected = collectUpstreamEntries(entry, resolveEntry);
  return expandStarReexportBindings(collected).find((row) => row.relPath === entry.relPath) ?? entry;
}

/** Materialize `export * from` bindings as explicit re-export rows for each exported symbol. */
export function expandStarReexportBindings(collected: SymbolIndexFileEntry[]): SymbolIndexFileEntry[] {
  const entriesByPath = new Map(collected.map((entry) => [entry.relPath, entry]));

  return collected.map((entry) => {
    const stars = entry.imports.filter((binding) => binding.importKind === 'star');
    if (stars.length === 0) return entry;

    const localNames = existingLocalNames(entry.imports);
    const materialized: SymbolImportBinding[] = [];

    for (const star of stars) {
      if (!star.sourceFilePath) continue;
      const exportable = resolveExportableNames(star.sourceFilePath, entriesByPath, new Set());
      for (const [localName, info] of exportable) {
        if (localName === STAR_LOCAL_NAME || localNames.has(localName)) continue;
        localNames.add(localName);
        materialized.push({
          localName,
          sourceName: info.sourceName,
          moduleSpecifier: star.moduleSpecifier,
          sourceFilePath: info.sourceFilePath,
          importKind: star.importKind === 'type' ? 'type' : 'reexport',
          startLine: star.startLine,
          startColumn: star.startColumn,
        });
      }
    }

    if (materialized.length === 0) return entry;
    return { ...entry, imports: [...entry.imports, ...materialized] };
  });
}
